/**
 * Groups the My Cases task inbox by owning record (PTV2-1321 bonus view).
 *
 * The grouped view loads a page of the caller's tasks and clusters them
 * client-side by their `entityGuid`, so all tasks for one patient / family sit
 * together. Tasks within a group keep the shared due-then-priority order; the
 * groups themselves are ordered by {@link sortGroups} — the inbox's sort control
 * shifts the patient headers, not the tasks under them.
 */

import {
  compareByDueThenPriority,
  PRIORITY_ORDER,
} from '@/features/patient-record/components/panels/TasksPanel/taskHelpers';
import type { TaskEntityType, TaskInboxItemViewModel } from '@/features/patient-record/types';

import type { TaskSortOption } from './taskInboxSort';

const NO_ENTITY_KEY = '__none__';

export interface TaskGroup {
  /** Stable key for React lists — the entity guid, or a sentinel when absent. */
  readonly key: string;
  readonly entityGuid: string | null;
  readonly entityType: TaskEntityType;
  /** Resolved record name, or `null` while the lookup is pending / unavailable. */
  readonly name: string | null;
  readonly tasks: readonly TaskInboxItemViewModel[];
}

export function groupTasksByEntity(
  tasks: readonly TaskInboxItemViewModel[],
  names: ReadonlyMap<string, string>,
): readonly TaskGroup[] {
  const byKey = new Map<string, TaskInboxItemViewModel[]>();
  for (const task of tasks) {
    const key = task.entityGuid ?? NO_ENTITY_KEY;
    const bucket = byKey.get(key);
    if (bucket === undefined) {
      byKey.set(key, [task]);
    } else {
      bucket.push(task);
    }
  }

  const groups: TaskGroup[] = [];
  for (const [key, bucket] of byKey) {
    const first = bucket[0];
    if (first === undefined) continue;
    const entityGuid = first.entityGuid;
    groups.push({
      key,
      entityGuid,
      entityType: first.entityType,
      name: entityGuid !== null ? (names.get(entityGuid) ?? null) : null,
      tasks: [...bucket].sort(compareByDueThenPriority),
    });
  }

  return groups.sort(compareByName);
}

/**
 * Orders whole groups by the selected sort — task count, most-urgent task, or
 * earliest due date — falling back to record name so the order is stable.
 * Returns a new array; never mutates the input.
 */
export function sortGroups(
  groups: readonly TaskGroup[],
  option: TaskSortOption,
): readonly TaskGroup[] {
  return groups.slice().sort((a, b) => {
    const primary = compareGroupsByField(a, b, option);
    return primary !== 0 ? primary : compareByName(a, b);
  });
}

function compareGroupsByField(a: TaskGroup, b: TaskGroup, option: TaskSortOption): number {
  if (option.field === 'taskCount') {
    const diff = a.tasks.length - b.tasks.length;
    return option.direction === 'desc' ? -diff : diff;
  }
  if (option.field === 'priority') {
    // Rank a group by its most-urgent task (lowest PRIORITY_ORDER value).
    const diff = groupPriorityRank(a) - groupPriorityRank(b);
    return option.direction === 'desc' ? diff : -diff;
  }
  // Due date: rank by the group's earliest due date; groups with none sink last.
  const ad = groupEarliestDue(a);
  const bd = groupEarliestDue(b);
  if (ad === null || bd === null) {
    if (ad === bd) return 0;
    return ad === null ? 1 : -1;
  }
  const delta = new Date(ad).getTime() - new Date(bd).getTime();
  return option.direction === 'asc' ? delta : -delta;
}

function groupPriorityRank(group: TaskGroup): number {
  let best = Number.POSITIVE_INFINITY;
  for (const task of group.tasks) best = Math.min(best, PRIORITY_ORDER[task.priority]);
  return best;
}

function groupEarliestDue(group: TaskGroup): string | null {
  let earliest: string | null = null;
  for (const task of group.tasks) {
    if (task.dueDate === null) continue;
    if (earliest === null || task.dueDate < earliest) earliest = task.dueDate;
  }
  return earliest;
}

/** Named groups first (locale order); unresolved / no-record groups last. */
function compareByName(a: TaskGroup, b: TaskGroup): number {
  if (a.name !== null && b.name !== null) {
    return a.name.localeCompare(b.name, undefined, { sensitivity: 'base' });
  }
  if (a.name !== null) return -1;
  if (b.name !== null) return 1;
  return 0;
}
